import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api from '@/lib/api';
import { setDisplayCurrency } from '@/lib/currency';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, Edit2, Sparkles, AlertCircle, Mail, CheckCircle, XCircle, ShieldAlert, FileText, Download, AlertTriangle, CreditCard, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const CATEGORIES = [
  'Housing', 'Rent', 'Mortgage', 'Utilities', 'Internet', 'Mobile Bill',
  'Groceries', 'Food Takeout', 'Restaurants', 'Coffee',
  'Transportation', 'Fuel / Gas', 'Car Maintenance', 'Car Insurance', 'Vehicle EMI', 'Public Transit',
  'Loan Repayment', 'Student Loan', 'Personal Loan', 'Credit Card Payments',
  'Insurance', 'Health Insurance', 'Life Insurance',
  'Gym Membership', 'Subscriptions', 'Streaming Services',
  'Shopping', 'Entertainment', 'Travel', 'Healthcare',
  'Investments', 'Savings', 'Education', 'Charity', 'Miscellaneous',
];

export default function SettingsPage() {
  const { logout, user: authUser } = useAuth();
  const navigate = useNavigate();
  const [budgets, setBudgets] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [profile, setProfile] = useState({
    name: '', username: '', email: '', email_verified: false,
    tax_enabled: false, tax_percentage: 0, base_currency: 'CAD', country_code: '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);

  // Danger Zone / Legal state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [dataRequestOpen, setDataRequestOpen] = useState(false);
  const [dataRequestType, setDataRequestType] = useState('access');
  const [dataRequestDetails, setDataRequestDetails] = useState('');
  const [dataRequestSending, setDataRequestSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [budgetsRes, rulesRes, profileRes] = await Promise.all([
        api.get('/api/category-budgets'),
        api.get('/api/merchant-rules'),
        api.get('/api/auth/me'),
      ]);
      setBudgets(budgetsRes.data || []);
      setRules(rulesRes.data || []);
      const u = profileRes.data;
      setProfile({
        name: u.name || '',
        username: u.username || '',
        email: u.email || '',
        email_verified: u.email_verified || false,
        tax_enabled: u.tax_enabled || false,
        tax_percentage: u.tax_percentage || 0,
        base_currency: u.base_currency || 'CAD',
        country_code: u.country_code || '',
      });
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBudget = async (b) => {
    try {
      await api.put(`/api/category-budgets/${b._id}`, { ...b, enabled: !b.enabled });
      load();
    } catch { toast.error('Failed to update'); }
  };

  const deleteBudget = async (id) => {
    if (!window.confirm('Delete this cap?')) return;
    try { await api.delete(`/api/category-budgets/${id}`); load(); }
    catch { toast.error('Failed to delete'); }
  };

  if (loading) return <div className="h-32 bg-muted rounded animate-pulse" />;

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') { toast.error('Type DELETE (all caps) to confirm.'); return; }
    setDeleting(true);
    try {
      const res = await api.delete('/api/auth/delete-account', {
        data: { password: deletePassword, confirmation: 'DELETE' },
      });
      if (res.data.billing_note) toast.warning(`Account deleted. Note: ${res.data.billing_note}`);
      else toast.success('Account permanently deleted.');
      await logout?.();
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Deletion failed. Please try again or contact support.');
    } finally { setDeleting(false); }
  };

  const handleDataRequest = async () => {
    setDataRequestSending(true);
    try {
      await api.post('/api/auth/data-request', { request_type: dataRequestType, details: dataRequestDetails });
      toast.success('Request submitted. We will respond within 30 days.');
      setDataRequestOpen(false);
      setDataRequestDetails('');
    } catch { toast.error('Failed. Please email info@themoneymap.online directly.'); }
    finally { setDataRequestSending(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">Profile, tax tracking, budget caps and learned categorization</p>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader className="pb-3"><CardTitle className="font-['Outfit'] text-base">Profile</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setProfileSaving(true);
            try {
              const payload = { name: profile.name, username: profile.username, tax_enabled: profile.tax_enabled, tax_percentage: parseFloat(profile.tax_percentage) || 0, base_currency: profile.base_currency, country_code: profile.country_code || null };
              await api.patch('/api/auth/me', payload);
              if (profile.base_currency) setDisplayCurrency(profile.base_currency);
              toast.success(`Profile saved · UI now in ${profile.base_currency}`);
              await load();
            } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
            finally { setProfileSaving(false); }
          }} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Name</Label><Input data-testid="profile-name-input" value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} required /></div>
              <div className="space-y-1"><Label className="text-xs">Username</Label><Input data-testid="profile-username-input" value={profile.username} onChange={(e) => setProfile((p) => ({ ...p, username: e.target.value }))} required /></div>
            </div>
            <div className="flex items-center gap-3 p-2.5 rounded-md bg-muted/30">
              <Switch checked={profile.tax_enabled} onCheckedChange={(v) => setProfile((p) => ({ ...p, tax_enabled: v }))} data-testid="tax-enabled-toggle" />
              <div className="flex-1">
                <Label className="text-xs font-medium">Tax tracking</Label>
                <p className="text-[11px] text-muted-foreground">For contractors / non-PAYE income. Dashboard will show Net Income and YTD Tax Owed.</p>
              </div>
              {profile.tax_enabled && (
                <div className="flex items-center gap-1.5">
                  <Input data-testid="tax-percentage-input" type="number" step="0.5" min="0" max="100" value={profile.tax_percentage} onChange={(e) => setProfile((p) => ({ ...p, tax_percentage: e.target.value }))} className="w-20 h-8 text-sm" />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
              )}
            </div>
            <Button type="submit" size="sm" disabled={profileSaving} data-testid="profile-save-btn">{profileSaving ? 'Saving...' : 'Save Profile'}</Button>
          </form>
        </CardContent>
      </Card>

      {/* Email & Security */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-['Outfit'] text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email &amp; Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">Email Address</Label>
            <div className="flex items-center gap-2 p-3 rounded-md border bg-muted/30">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="flex-1 text-sm">{profile.email || 'No email set'}</span>
              {profile.email && (
                profile.email_verified ? (
                  <Badge variant="success" className="text-[10px] gap-1"><CheckCircle className="h-3 w-3" />Verified</Badge>
                ) : (
                  <Badge variant="destructive" className="text-[10px] gap-1"><XCircle className="h-3 w-3" />Unverified</Badge>
                )
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              {profile.email_verified
                ? 'Your email is verified. You can use password reset and 2FA features.'
                : 'Verify your email to enable password reset and two-factor authentication.'}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {!profile.email_verified && profile.email && (
              <Button type="button" size="sm" variant="outline" onClick={async () => {
                try { await api.post('/api/auth/resend-verification'); toast.success('Verification email sent! Check your inbox.'); }
                catch (err) { toast.error(err?.response?.data?.detail || 'Failed to send verification email'); }
              }}>
                Resend Verification Email
              </Button>
            )}
            <Button type="button" size="sm" variant="outline" onClick={() => window.location.href = '/update-email'}>
              {profile.email ? 'Change Email' : 'Add Email'}
            </Button>
          </div>
          {profile.email_verified && (
            <div className="p-3 rounded-md bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
              <p className="text-xs text-green-700 dark:text-green-300">
                ✓ Password reset enabled · ✓ 2FA ready · ✓ Security notifications active
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Region & Currency */}
      <Card data-testid="region-currency-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-['Outfit'] text-base">Region &amp; Currency</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Default Currency</Label>
              <Select value={profile.base_currency} onValueChange={(currency) => {
                const currencyToCountry = { 'INR': 'IN', 'CAD': 'CA', 'USD': 'US', 'AED': 'AE', 'AUD': 'AU', 'EUR': 'FR', 'GBP': 'GB', 'SGD': 'SG', 'JPY': 'JP' };
                setProfile((p) => ({ ...p, base_currency: currency, country_code: currencyToCountry[currency] || 'US' }));
              }}>
                <SelectTrigger data-testid="base-currency-select" className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[
                    { code: 'INR', label: 'INR - Indian Rupee (₹)' }, { code: 'CAD', label: 'CAD - Canadian Dollar (C$)' },
                    { code: 'USD', label: 'USD - US Dollar ($)' }, { code: 'AED', label: 'AED - UAE Dirham' },
                    { code: 'AUD', label: 'AUD - Australian Dollar (A$)' }, { code: 'EUR', label: 'EUR - Euro (€)' },
                    { code: 'GBP', label: 'GBP - British Pound (£)' }, { code: 'SGD', label: 'SGD - Singapore Dollar' },
                    { code: 'JPY', label: 'JPY - Japanese Yen (¥)' },
                  ].map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Sets your country automatically for region-specific features</p>
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <Button type="button" size="sm" variant="ghost" disabled={detecting} data-testid="detect-currency-btn"
                onClick={async () => {
                  setDetecting(true);
                  try {
                    const { data } = await api.get('/api/auth/detect-location');
                    setProfile((p) => ({ ...p, base_currency: data.currency || p.base_currency, country_code: data.country_code || p.country_code }));
                    toast.success(`Detected: ${data.country} → ${data.currency}`);
                  } catch { toast.error('Detection failed'); } finally { setDetecting(false); }
                }} className="text-xs text-muted-foreground hover:text-foreground">
                {detecting ? 'Detecting…' : '🌍 Auto-detect my location'}
              </Button>
              <Button type="button" size="sm" disabled={profileSaving} data-testid="save-region-btn"
                onClick={async () => {
                  setProfileSaving(true);
                  try {
                    await api.patch('/api/auth/me', { base_currency: profile.base_currency, country_code: profile.country_code || null });
                    if (profile.base_currency) setDisplayCurrency(profile.base_currency);
                    toast.success(`Region saved · UI now in ${profile.base_currency}`);
                    await load();
                  } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
                  finally { setProfileSaving(false); }
                }}>
                {profileSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Category Budgets */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="font-['Outfit'] text-base">Category Spending Caps</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Set max % of monthly income per category. Dashboard shows warning when exceeded.</p>
          </div>
          <Dialog open={showAdd || !!editItem} onOpenChange={(o) => { if (!o) { setShowAdd(false); setEditItem(null); } else setShowAdd(true); }}>
            <DialogTrigger asChild><Button size="sm" data-testid="add-budget-btn"><Plus className="h-4 w-4 mr-1" />Add Cap</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-['Outfit']">{editItem ? 'Edit Cap' : 'Add Category Cap'}</DialogTitle></DialogHeader>
              <BudgetForm item={editItem} onSave={async () => { await load(); setShowAdd(false); setEditItem(null); }} existingCategories={budgets.map((b) => b.category)} />
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {budgets.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No caps yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs">Max %</TableHead>
                  <TableHead className="text-xs">Enabled</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {budgets.map((b) => (
                  <TableRow key={b._id} data-testid={`budget-row-${b._id}`}>
                    <TableCell className="text-sm font-medium">{b.category}</TableCell>
                    <TableCell className="text-sm tabular-nums">{b.max_pct}%</TableCell>
                    <TableCell><Switch checked={b.enabled} onCheckedChange={() => toggleBudget(b)} data-testid={`budget-toggle-${b._id}`} /></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditItem(b)} data-testid={`budget-edit-${b._id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteBudget(b._id)} data-testid={`budget-delete-${b._id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Learned Merchant Rules */}
      <Card>
        <CardHeader>
          <CardTitle className="font-['Outfit'] text-base flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-amber-500" />
            Adaptive AI — Learned Rules
            <Badge variant="secondary" className="text-xs ml-2" data-testid="learned-rules-count">{rules.length}</Badge>
          </CardTitle>
          <p className="text-xs text-muted-foreground">When you re-categorize a transaction, the app learns and applies the rule to future matching merchants.</p>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <div className="flex items-start gap-2 p-3 rounded-md bg-muted/50">
              <AlertCircle className="h-4 w-4 text-muted-foreground mt-0.5" />
              <p className="text-xs text-muted-foreground">No learned rules yet. Edit any transaction&apos;s category and the AI will remember for next time.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Merchant Pattern</TableHead>
                  <TableHead className="text-xs">Category</TableHead>
                  <TableHead className="text-xs text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.map((r) => (
                  <TableRow key={r._id} data-testid={`rule-row-${r._id}`}>
                    <TableCell className="text-sm">{r.pattern}</TableCell>
                    <TableCell><Badge variant="secondary" className="text-xs">{r.category}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={async () => {
                        if (!window.confirm('Forget this rule?')) return;
                        try { await api.delete(`/api/merchant-rules/${r._id}`); load(); } catch { toast.error('Failed'); }
                      }} data-testid={`rule-delete-${r._id}`}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Subscription */}
      <Card data-testid="subscription-card">
        <CardHeader className="pb-3">
          <CardTitle className="font-['Outfit'] text-base flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Subscription
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {authUser?.subscription_status === 'active' ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <span className="text-xs text-muted-foreground">Status</span>
                {authUser.subscription_cancel_at ? (
                  <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                    Cancels {new Date(authUser.subscription_cancel_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">Pro — Active</Badge>
                )}
              </div>
              {authUser.subscription_current_period_end && !authUser.subscription_cancel_at && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                  <span className="text-xs text-muted-foreground">Next renewal</span>
                  <span className="text-xs font-medium">
                    {new Date(authUser.subscription_current_period_end * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
              )}
              <Link to="/support" className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:underline">
                <ExternalLink className="h-3 w-3" />
                Manage or cancel subscription
              </Link>
            </div>
          ) : authUser?.is_trial_active ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <span className="text-xs text-muted-foreground">Status</span>
                <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">
                  Free trial — {authUser.trial_days_left ?? '?'} days left
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground">
                Trial ends on {authUser.trial_end_date
                  ? new Date(authUser.trial_end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : '—'}. No charge if you don&apos;t subscribe.
              </p>
              <Link to="/support" className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:underline">
                <ExternalLink className="h-3 w-3" />
                Upgrade to Pro
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <span className="text-xs text-muted-foreground">Status</span>
                <Badge variant="secondary" className="text-xs">Free</Badge>
              </div>
              <Link to="/support" className="inline-flex items-center gap-1.5 text-xs text-blue-500 hover:underline">
                <ExternalLink className="h-3 w-3" />
                Upgrade to Pro
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legal & Privacy */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-['Outfit'] text-base flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Legal &amp; Privacy
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to="/terms" target="_blank" className="text-blue-500 hover:underline">Terms of Service</Link>
            <span className="text-muted-foreground">·</span>
            <Link to="/privacy" target="_blank" className="text-blue-500 hover:underline">Privacy Policy</Link>
          </div>
          <p className="text-xs text-muted-foreground">
            Under PIPEDA and Québec Law 25, you have the right to access, correct, or export your personal data.
          </p>
          <Dialog open={dataRequestOpen} onOpenChange={setDataRequestOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="data-request-btn">
                <Download className="h-3.5 w-3.5 mr-2" />
                Request My Data
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Personal Data Request</DialogTitle>
                <DialogDescription>
                  We respond within 30 days. For urgent matters email{' '}
                  <a href="mailto:info@themoneymap.online" className="text-blue-500">info@themoneymap.online</a>.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1">
                  <Label className="text-sm">Request type</Label>
                  <Select value={dataRequestType} onValueChange={setDataRequestType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="access">Access — send me a copy of my data</SelectItem>
                      <SelectItem value="correction">Correction — correct inaccurate information</SelectItem>
                      <SelectItem value="portability">Portability — export in machine-readable format</SelectItem>
                      <SelectItem value="other">Other inquiry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Additional details (optional)</Label>
                  <Input placeholder="Any specific information or corrections..." value={dataRequestDetails}
                    onChange={(e) => setDataRequestDetails(e.target.value)} data-testid="data-request-details" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDataRequestOpen(false)}>Cancel</Button>
                <Button onClick={handleDataRequest} disabled={dataRequestSending} data-testid="data-request-submit">
                  {dataRequestSending ? 'Sending...' : 'Submit Request'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="border-destructive/30">
        <CardHeader className="pb-3">
          <CardTitle className="font-['Outfit'] text-base flex items-center gap-2 text-destructive">
            <ShieldAlert className="h-4 w-4" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 space-y-2">
            <p className="text-sm font-medium">Delete Account</p>
            <p className="text-xs text-muted-foreground">
              Permanently deletes your account and all financial data. Any active subscription is cancelled immediately. This cannot be undone.
            </p>
            <Dialog open={showDeleteDialog} onOpenChange={(v) => {
              setShowDeleteDialog(v);
              if (!v) { setDeletePassword(''); setDeleteConfirmText(''); }
            }}>
              <DialogTrigger asChild>
                <Button variant="destructive" size="sm" data-testid="delete-account-btn">
                  <Trash2 className="h-3.5 w-3.5 mr-2" />
                  Delete My Account
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-destructive">
                    <AlertTriangle className="h-5 w-5" />
                    Permanently Delete Account
                  </DialogTitle>
                  <DialogDescription>
                    This will cancel any active subscription, delete all your financial data, and prevent future login. This cannot be undone.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-1">
                    <Label className="text-sm">Current password</Label>
                    <Input type="password" placeholder="Enter your current password" value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)} data-testid="delete-password-input" />
                    <p className="text-xs text-muted-foreground">Google sign-in users: leave this blank.</p>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm">Type <strong>DELETE</strong> to confirm</Label>
                    <Input placeholder="DELETE" value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)} data-testid="delete-confirm-input" />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>Keep My Account</Button>
                  <Button variant="destructive" onClick={handleDeleteAccount}
                    disabled={deleting || deleteConfirmText !== 'DELETE'} data-testid="delete-account-confirm-btn">
                    {deleting ? 'Deleting...' : 'Yes, Delete Permanently'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function BudgetForm({ item, onSave, existingCategories }) {
  const [form, setForm] = useState({
    category: item?.category || '',
    max_pct: item?.max_pct ?? 10,
    enabled: item?.enabled ?? true,
    notes: item?.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error('Pick a category'); return; }
    setSaving(true);
    try {
      const payload = { ...form, max_pct: parseFloat(form.max_pct) };
      if (item) await api.put(`/api/category-budgets/${item._id}`, payload);
      else await api.post('/api/category-budgets', payload);
      toast.success('Saved');
      await onSave();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };
  const availableCats = item ? CATEGORIES : CATEGORIES.filter((c) => !existingCategories.includes(c));
  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="space-y-1">
        <Label className="text-xs">Category</Label>
        <Select value={form.category} onValueChange={(v) => set('category', v)} disabled={!!item}>
          <SelectTrigger data-testid="budget-category-select"><SelectValue placeholder="Select category..." /></SelectTrigger>
          <SelectContent>{availableCats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Max % of Monthly Income</Label>
        <Input data-testid="budget-pct-input" type="number" step="0.5" min="0" max="100" value={form.max_pct} onChange={(e) => set('max_pct', e.target.value)} required />
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="budget-enabled-toggle" />
        <Label className="text-xs">Enabled</Label>
      </div>
      <Button type="submit" className="w-full" disabled={saving} data-testid="budget-submit">{saving ? 'Saving...' : 'Save'}</Button>
    </form>
  );
}
